import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Truck, Building, User as UserIcon, Bell } from "lucide-react";
import { toast } from "sonner";
import PushSettings from "@/components/PushSettings";

export default function EditProfile() {
  const { user, updateMe, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    phone_number: user?.phone_number || "",
    bio: user?.bio || "",
    city: user?.city || "",
    vehicle_type: user?.vehicle_type || "",
    vehicle_description: user?.vehicle_description || "",
    license_plate: user?.license_plate || "",
    specialties: (user?.specialties || []).join(", "),
    bank_reg: user?.bank_reg || "",
    bank_account: user?.bank_account || "",
  });

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, specialties: form.specialties.split(",").map(s => s.trim()).filter(Boolean) };
      await updateMe(payload);
      toast.success("Profil opdateret");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Er du sikker? Din konto og data slettes permanent.")) return;
    await api.delete("/auth/me");
    await logout();
    navigate("/");
    toast.success("Konto slettet");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl font-bold mb-6">Rediger profil</h1>

      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><UserIcon className="w-4 h-4" /> Personlige oplysninger</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Fulde navn"><Input value={form.full_name} onChange={(e) => upd("full_name", e.target.value)} /></Field>
            <Field label="Telefon"><Input value={form.phone_number} onChange={(e) => upd("phone_number", e.target.value)} placeholder="+45 12 34 56 78" /></Field>
            <Field label="By"><Input value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="København" /></Field>
            <Field label="Om mig"><Textarea rows={3} value={form.bio} onChange={(e) => upd("bio", e.target.value)} placeholder="Kort præsentation..." /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Truck className="w-4 h-4" /> Chauffør (valgfrit)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Field label="Køretøjstype">
              <Select value={form.vehicle_type} onValueChange={(v) => upd("vehicle_type", v)}>
                <SelectTrigger data-testid="vehicle-type-select"><SelectValue placeholder="Vælg transportmiddel" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ladcykel">🚲 Ladcykel (grøn transport)</SelectItem>
                  <SelectItem value="Cykel med trailer">🚲 Cykel med trailer (grøn transport)</SelectItem>
                  <SelectItem value="Personbil">🚗 Personbil</SelectItem>
                  <SelectItem value="Stationcar">🚙 Stationcar</SelectItem>
                  <SelectItem value="Pickup">🛻 Pickup</SelectItem>
                  <SelectItem value="Kassevogn">🚚 Kassevogn</SelectItem>
                  <SelectItem value="Varevogn med lift">🚚 Varevogn med lift</SelectItem>
                  <SelectItem value="Trailer">🚗 Bil med trailer</SelectItem>
                  <SelectItem value="Lastbil">🚛 Lastbil</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Beskrivelse af køretøj"><Input value={form.vehicle_description} onChange={(e) => upd("vehicle_description", e.target.value)} placeholder="Mercedes Sprinter, 14 m³" /></Field>
            <Field label="Nummerplade"><Input value={form.license_plate} onChange={(e) => upd("license_plate", e.target.value)} /></Field>
            <Field label="Specialer (komma-separeret)"><Input value={form.specialties} onChange={(e) => upd("specialties", e.target.value)} placeholder="waste_removal, furniture_disposal" /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building className="w-4 h-4" /> Bankoplysninger (til udbetaling)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Reg. nr."><Input value={form.bank_reg} onChange={(e) => upd("bank_reg", e.target.value)} placeholder="1234" maxLength={4} /></Field>
              <Field label="Konto nr."><Input value={form.bank_account} onChange={(e) => upd("bank_account", e.target.value)} placeholder="1234567890" /></Field>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="save-profile-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Gem ændringer
        </Button>
      </form>

      <div className="mt-8 space-y-4">
        <h2 className="font-semibold text-lg flex items-center gap-2"><Bell className="w-4 h-4" /> Notifikationer</h2>
        <PushSettings />
      </div>

      <Card className="mt-8 border-destructive/30">
        <CardContent className="py-6">
          <h3 className="font-semibold text-destructive mb-2">Slet konto</h3>
          <p className="text-sm text-muted-foreground mb-3">Din konto og alle data slettes permanent. Denne handling kan ikke fortrydes.</p>
          <Button variant="destructive" onClick={deleteAccount} data-testid="delete-account-btn">Slet min konto</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
